import { combineEpics } from "redux-observable";
import { of, Subject, Subscriber, throwError } from "rxjs";
import { ajax } from "rxjs/ajax";
import { catchError, filter, map, merge, mergeMap, takeUntil } from "rxjs/operators";

import { actionCreators, isOfAction } from "../actions";
import { getMaxUploadsSizeBytes } from "../reducers/config";
import { getToken } from "../reducers/persist";
import { getFlattenRoomViews } from "../reducers/rooms";
import { executeEffect } from "../utils/effects";
import { isWrongToken, retryNonFatalAjax } from "../utils/errors";
import { Epic } from "../utils/types";

const UploadObservable = (
    apiBase: string | undefined,
    roomId: string,
    token: string,
    file: Blob,
    progress$?: Subject<number>
) => {
    const formData = new FormData();
    formData.append("file", file);

    const progressSubscriber = progress$
        ? new Subscriber<ProgressEvent>(
              ({ loaded, total }) => {
                  progress$.next(loaded / (total || file.size || 1));
              },
              () => {
                  progress$.next(0); // reset progress on error
              },
              () => {
                  progress$.complete();
              }
          )
        : undefined;

    const ajax$ = ajax({
        method: "POST",
        url: apiBase + "/file/v1?room_id=" + roomId,
        headers: { Authorization: "Bearer " + token },
        body: formData,
        progressSubscriber,
    }).pipe(mergeMap(x => (x.status === 200 ? of(x) : throwError(x))));
    return ajax$;
};

const onUploadFiles: Epic = (action$, state$, { App }) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.uploadFiles)),
        mergeMap(({ payload: { roomView, uploads } }) => {
            const token = getToken(state$.value);
            if (token) {
                return uploads
                    .map((file, tag) => ({ roomView, file, tag, token }))
                    .valueSeq()
                    .toArray();
            }
            return of<never>();
        }),
        mergeMap(({ file, roomView, tag, token }) => {
            const maxFileUploadSize = getMaxUploadsSizeBytes(state$.value);
            if (file.size > maxFileUploadSize) {
                return of(
                    actionCreators.uploadFile({
                        roomView,
                        tag,
                        status: "error",
                        err: {
                            type: "file_upload_too_big",
                            fileSize: file.size,
                            fileName: file instanceof File ? file.name : undefined,
                        },
                    })
                );
            }
            const progress$ = new Subject<number>();
            return executeEffect(() =>
                UploadObservable(App.proto.getApiBase(), roomView.roomId, token, file, progress$)
            ).pipe(
                mergeMap(({ result$ }) =>
                    result$.pipe(
                        retryNonFatalAjax((err, retry) => {
                            const roomIsStillOpen = getFlattenRoomViews(state$.value).some(
                                x => x.roomId === roomView.roomId
                            );
                            if (roomIsStillOpen) {
                                return retry;
                            } else {
                                throw err;
                            }
                        }),
                        map(({ response }) =>
                            actionCreators.uploadFile({
                                roomView,
                                tag,
                                status: "done",
                                response,
                            })
                        ),
                        merge(
                            progress$.pipe(
                                map(progress =>
                                    actionCreators.uploadFile({
                                        roomView,
                                        tag,
                                        status: "progress",
                                        progress,
                                    })
                                )
                            )
                        )
                    )
                ),
                takeUntil(
                    action$.pipe(
                        filter(isOfAction(actionCreators.uploadFile)),
                        filter(
                            ({ payload: { status, tag: cancelTag } }) =>
                                status === "cancel" && cancelTag === tag
                        )
                    )
                ),
                catchError(err => {
                    if (isWrongToken(err)) {
                        App.actionCreators.onWrongToken();
                    }
                    console.error("failed to upload", roomView, file, err);
                    return of(actionCreators.uploadFile({ roomView, tag, status: "error", err }));
                })
            );
        })
    );
};

export const uploadFilesEpic = combineEpics(onUploadFiles);
